/**
 * Core analytics dispatch. {@link trackEvent} and {@link trackPageview} fan a
 * single call out to GA4 (`window.gtag`) and Facebook Pixel (`window.fbq`),
 * each guarded by its configured ID and the presence of the global. Both are
 * safe no-ops during SSR (`window` undefined) and when nothing is configured,
 * so call sites never need their own guards.
 */
import { reportHandledException } from '@/lib/error-reporting'
import { getOrCreateDeviceId } from '../identity'
import { fbPixelId, gaMeasurementId } from './config'
import { PIXEL_EVENT_MAP, type AnalyticsEventName } from './events'
import { isOnAnalyticsGaOnlyPath } from './excluded-paths'
import type { AnalyticsParams } from './params'
import { mirrorEventToServer } from './server-mirror'

/** Emitted after each configured browser analytics snippet installs its queue. */
export const ANALYTICS_READY_EVENT = 'kiao:analytics-ready'

export type { AnalyticsParams } from './params'

type GtagFn = (...args: unknown[]) => void
type FbqFn = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void
  queue?: unknown[]
}

// Clarity is intentionally NOT typed/dispatched here: it's a passive
// session-replay/heatmap tag (loaded in analytics-scripts.tsx) that auto-
// captures with no explicit event calls, so there is no window.clarity path
// for this module to use.
declare global {
  interface Window {
    gtag?: GtagFn
    dataLayer?: unknown[]
    fbq?: FbqFn
    _fbq?: FbqFn
  }
}

const hasGa = (): boolean =>
  typeof window !== 'undefined' && gaMeasurementId().length > 0 && typeof window.gtag === 'function'

const hasPixel = (): boolean =>
  typeof window !== 'undefined' && fbPixelId().length > 0 && typeof window.fbq === 'function'

/**
 * Pixel dispatch is refused on GA-only paths (`excluded-paths.ts`) at the
 * moment of dispatch, so a queue installed on an earlier page cannot report
 * from a survey page, and a shared component there (the sign-in form) sends
 * its conversion to GA alone.
 */
const pixelAllowedHere = (): boolean => hasPixel() && !isOnAnalyticsGaOnlyPath()

/**
 * Returns true once every analytics destination configured for this page has
 * installed its browser-side queue. Calls made after this point cannot be lost
 * merely because Next.js has not yet run an `afterInteractive` snippet.
 */
export const isAnalyticsDispatchReady = (): boolean => {
  const gaReady = gaMeasurementId().length === 0 || hasGa()
  // A GA-only path never loads the Pixel, so it must not wait for it.
  const pixelReady = fbPixelId().length === 0 || isOnAnalyticsGaOnlyPath() || hasPixel()
  return gaReady && pixelReady
}

/**
 * Stamps the site's anonymous id onto a GA payload.
 *
 * GA's own `_ga` cookie already identifies a browser, but it is GA's and it
 * cannot be joined to anything else: the API stores `kv_did`, so reporting the
 * same value as an event param is what lets a GA funnel and a DB row be shown
 * to be the same person. An explicit `anon_id` from the caller wins, and a
 * missing id (SSR, cookies blocked) is omitted rather than sent empty — GA
 * treats an empty string as a real value and it would collapse every such
 * visitor into one bucket.
 */
function withAnonId(params: Record<string, unknown>): Record<string, unknown> {
  if (params.anon_id !== undefined) return params
  const anonId = getOrCreateDeviceId()
  return anonId ? { ...params, anon_id: anonId } : params
}

/**
 * Low-level GA4 dispatch — sends a `gtag('event', …)` when GA is configured and
 * loaded, else no-ops. Accepts rich payloads (nested `items` arrays etc.) for
 * GA4 ecommerce. Prefer {@link trackEvent} for the simple semantic taxonomy;
 * the commerce helpers use this directly for platform-divergent payloads.
 *
 * Every GA event leaves through here, which is why {@link withAnonId} is
 * applied at this single point rather than at each call site.
 */
export function gaEvent(name: string, params: Record<string, unknown> = {}): void {
  if (!hasGa()) return
  try {
    window.gtag!('event', name, withAnonId(params))
  } catch (cause) {
    reportHandledException(cause, { source: 'lib/analytics/track.ts' })
    // Most call sites fire trackEvent BEFORE navigating / updating state, so a
    // throw from the third-party gtag must never break the UI. Report + continue.
    console.warn('[analytics] gtag dispatch failed:', cause)
  }
}

/**
 * Low-level Pixel dispatch — `track` for standard events, `trackCustom`
 * otherwise. Pass `options.eventID` to share a dedup id with a matching
 * server-side Conversions API event (Meta counts the conversion once).
 */
export function pixelEvent(
  eventName: string,
  standard: boolean,
  params: Record<string, unknown> = {},
  options?: { eventID?: string }
): void {
  if (!pixelAllowedHere()) return
  try {
    const method = standard ? 'track' : 'trackCustom'
    if (options?.eventID) {
      window.fbq!(method, eventName, params, { eventID: options.eventID })
    } else {
      window.fbq!(method, eventName, params)
    }
  } catch (cause) {
    reportHandledException(cause, { source: 'lib/analytics/track.ts' })
    // Same guard as gaEvent — a misbehaving fbq must not break the caller's
    // click handler. Report + continue.
    console.warn('[analytics] fbq dispatch failed:', cause)
  }
}

/**
 * Fire a semantic conversion event to GA4 and Facebook Pixel. The Pixel side
 * picks standard (`track`) vs custom (`trackCustom`) from {@link PIXEL_EVENT_MAP}.
 *
 * Only the GA payload carries the anonymous id. The Pixel receives it as
 * `external_id` through Advanced Matching on the base snippet, where Meta
 * actually uses it for identity resolution; repeating it per event would just
 * be an unused custom property.
 */
export function trackEvent(name: AnalyticsEventName, params: AnalyticsParams = {}): void {
  gaEvent(name, params)
  const mapping = PIXEL_EVENT_MAP[name]
  pixelEvent(mapping.name, mapping.standard, params)
  // A short whitelist of pre-signup moments is ALSO written to our own
  // database, keyed by `kv_did`, so a later signup can be joined back to what
  // this browser did — the one question GA cannot answer. Everything else is a
  // no-op; `server-mirror.ts` holds the list (and why the capability-URL guard
  // lives there and not here).
  mirrorEventToServer(name, params)
}

/**
 * Fire once every configured analytics queue is installed, rather than dropping
 * the event when it is not.
 *
 * {@link trackEvent} is a no-op while `gtag`/`fbq` are still loading, and both
 * load `afterInteractive` — i.e. after hydration. For a CLICK that loss is
 * theoretical (nobody clicks that fast). For an IMPRESSION it is not: an
 * element can already be in the viewport on first paint, and the event that
 * goes missing is the DENOMINATOR every rate on that funnel is divided by, so
 * losing it makes a campaign nobody saw look like one nobody wanted.
 *
 * The caller must still latch against firing twice; this only defers.
 */
export function trackEventWhenReady(name: AnalyticsEventName, params: AnalyticsParams = {}): void {
  if (typeof window === 'undefined') return
  if (isAnalyticsDispatchReady()) {
    trackEvent(name, params)
    return
  }
  const flush = () => {
    if (!isAnalyticsDispatchReady()) return
    window.removeEventListener(ANALYTICS_READY_EVENT, flush)
    trackEvent(name, params)
  }
  window.addEventListener(ANALYTICS_READY_EVENT, flush)
}

/**
 * Report a client-side route change as a pageview. The initial pageview is
 * fired by each platform's own init snippet (gtag config / fbq PageView), so
 * this is only used for SPA navigations after the first load.
 */
export function trackPageview(url: string): void {
  // Route through the same guarded primitives as trackEvent so a misbehaving
  // gtag/fbq can't break the route-change effect that calls this.
  gaEvent('page_view', {
    page_path: url,
    page_location: typeof window !== 'undefined' ? window.location.href : url,
    page_title: typeof document !== 'undefined' ? document.title : undefined,
  })
  pixelEvent('PageView', true)
}
